/**
 * CareSessionService
 * Service for managing care sessions with list views, per-case lookups and notes
 */
import { BaseService } from '../base/BaseService';
/**
 * Service for care session operations (enriched model views throughout)
 */
export class CareSessionService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? CareSessionService.DEFAULT_SERVICE_PATH,
            basePath: CareSessionService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    /**
     * Get the paged session list view (filtered)
     * @param filter - Optional filter parameters
     * @returns Paged session list view response
     */
    async listView(filter) {
        const response = await this.listViewRaw(filter);
        if (!response.data) {
            throw new Error('Failed to retrieve care session list view');
        }
        return response.data;
    }
    /**
     * Get the paged session list view (filtered) with full response
     * @param filter - Optional filter parameters
     * @returns ResponseData with paged session list view response
     */
    listViewRaw(filter) {
        const url = this.buildUrl('list-view');
        return this.http.post(url, filter ?? {});
    }
    /**
     * Get a session view by ID
     * @param id - The session ID
     * @returns Care session model view
     */
    async getById(id) {
        const response = await this.getByIdRaw(id);
        if (!response.data) {
            throw new Error(`Care session with ID ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get a session view by ID with full response
     * @param id - The session ID
     * @returns ResponseData with care session model view
     */
    getByIdRaw(id) {
        const url = this.buildUrl(String(id));
        return this.http.get(url);
    }
    /**
     * Get all sessions linked to a case
     * @param caseId - The case ID
     * @returns Array of care session model views
     */
    async byCase(caseId) {
        const response = await this.byCaseRaw(caseId);
        return response.data ?? [];
    }
    /**
     * Get all sessions linked to a case with full response
     * @param caseId - The case ID
     * @returns ResponseData with array of care session model views
     */
    byCaseRaw(caseId) {
        const url = this.buildUrl(`by-case/${caseId}`);
        return this.http.get(url);
    }
    /**
     * Create a care session (linking the given cases)
     * @param data - Create payload
     * @returns The created session as a model view
     */
    async create(data) {
        const response = await this.createRaw(data);
        if (!response.data) {
            throw new Error('Failed to create care session');
        }
        return response.data;
    }
    /**
     * Create a care session with full response
     * @param data - Create payload
     * @returns ResponseData with the created session model view
     */
    createRaw(data) {
        const url = this.buildUrl();
        return this.http.post(url, data);
    }
    /**
     * Update a care session
     * @param id - The session ID
     * @param data - Update payload
     * @returns The updated session as a model view
     */
    async update(id, data) {
        const response = await this.updateRaw(id, data);
        if (!response.data) {
            throw new Error(`Failed to update care session with ID ${id}`);
        }
        return response.data;
    }
    /**
     * Update a care session with full response
     * @param id - The session ID
     * @param data - Update payload
     * @returns ResponseData with the updated session model view
     */
    updateRaw(id, data) {
        const url = this.buildUrl(String(id));
        return this.http.put(url, data);
    }
    /**
     * Delete a care session
     * @param id - The session ID
     * @returns True if deleted successfully
     */
    async delete(id) {
        const response = await this.deleteRaw(id);
        return response.success;
    }
    /**
     * Delete a care session with full response
     * @param id - The session ID
     * @returns ResponseData (success/fail)
     */
    deleteRaw(id) {
        const url = this.buildUrl(String(id));
        return this.http.delete(url);
    }
    /**
     * Get the notes for a session
     * @param sessionId - The session ID
     * @returns Array of session notes
     */
    async notes(sessionId) {
        const response = await this.notesRaw(sessionId);
        return response.data ?? [];
    }
    /**
     * Get the notes for a session with full response
     * @param sessionId - The session ID
     * @returns ResponseData with array of session notes
     */
    notesRaw(sessionId) {
        const url = this.buildUrl(`${sessionId}/notes`);
        return this.http.get(url);
    }
    /**
     * Add a note to a session
     * @param sessionId - The session ID
     * @param data - Note payload
     * @returns The created session note
     */
    async addNote(sessionId, data) {
        const response = await this.addNoteRaw(sessionId, data);
        if (!response.data) {
            throw new Error(`Failed to add note to care session ${sessionId}`);
        }
        return response.data;
    }
    /**
     * Add a note to a session with full response
     * @param sessionId - The session ID
     * @param data - Note payload
     * @returns ResponseData with the created session note
     */
    addNoteRaw(sessionId, data) {
        const url = this.buildUrl(`${sessionId}/notes`);
        return this.http.post(url, data);
    }
    /**
     * Update a session note
     * @param sessionId - The session ID
     * @param noteId - The note ID
     * @param data - Partial note payload (only supplied fields are applied)
     * @returns The updated session note
     */
    async updateNote(sessionId, noteId, data) {
        const response = await this.updateNoteRaw(sessionId, noteId, data);
        if (!response.data) {
            throw new Error(`Failed to update note ${noteId} on care session ${sessionId}`);
        }
        return response.data;
    }
    /**
     * Update a session note with full response
     * @param sessionId - The session ID
     * @param noteId - The note ID
     * @param data - Partial note payload
     * @returns ResponseData with the updated session note
     */
    updateNoteRaw(sessionId, noteId, data) {
        const url = this.buildUrl(`${sessionId}/notes/${noteId}`);
        return this.http.put(url, data);
    }
    /**
     * Delete a session note
     * @param sessionId - The session ID
     * @param noteId - The note ID
     * @returns True if deleted successfully
     */
    async deleteNote(sessionId, noteId) {
        const response = await this.deleteNoteRaw(sessionId, noteId);
        return response.success;
    }
    /**
     * Delete a session note with full response
     * @param sessionId - The session ID
     * @param noteId - The note ID
     * @returns ResponseData (success/fail)
     */
    deleteNoteRaw(sessionId, noteId) {
        const url = this.buildUrl(`${sessionId}/notes/${noteId}`);
        return this.http.delete(url);
    }
    /**
     * Change only the session's status.
     * PATCH api/care-sessions/{id}/change-status/{newStatus}
     * Cancelling (-1) still publishes the CareSessionCancelledEvent server-side.
     * @param sessionId - The session ID
     * @param newStatus - The new StatusID
     * @returns The updated care session
     */
    async changeStatus(sessionId, newStatus) {
        const response = await this.changeStatusRaw(sessionId, newStatus);
        if (!response.data) {
            throw new Error(`Failed to change status for care session ${sessionId}`);
        }
        return response.data;
    }
    /**
     * Change only the session's status with full response
     * @param sessionId - The session ID
     * @param newStatus - The new StatusID
     * @returns ResponseData with the updated care session
     */
    changeStatusRaw(sessionId, newStatus) {
        const url = this.buildUrl(`${sessionId}/change-status/${newStatus}`);
        return this.http.patch(url);
    }
}
CareSessionService.DEFAULT_SERVICE_PATH = 'ac-counselling';
CareSessionService.BASE_PATH = 'api/care-sessions';
//# sourceMappingURL=CareSessionService.js.map